import type { CentralKpi, CentralPeriodKey } from '../services/MendsVisionService';

export const CENTRAL_PERIODS: Array<{ key: CentralPeriodKey; label: string }> = [
  { key: 'today', label: 'Hoje' },
  { key: '7d', label: 'Últimos 7 dias' },
  { key: '14d', label: 'Últimos 14 dias' },
  { key: '30d', label: 'Últimos 30 dias' },
];

export function parsePeriod(raw: string | null | undefined): CentralPeriodKey {
  return raw === 'today' || raw === '14d' || raw === '30d' ? raw : '7d';
}

const intFmt = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const decFmt = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

/** Minutos em texto curto: "45 s", "12 min", "2 h 5 min". */
export function formatMinutes(minutes: number): string {
  if (minutes < 1) return `${Math.max(1, Math.round(minutes * 60))} s`;
  if (minutes < 60) return `${decFmt.format(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

export function formatKpiValue(
  value: number | null | undefined,
  unit: CentralKpi['unit'],
  currency?: string | null,
): string {
  if (value === null || value === undefined) return '—';
  switch (unit) {
    case 'minutes':
      return formatMinutes(value);
    case 'percent':
      return `${decFmt.format(value)}%`;
    case 'currency':
      return currency
        ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value)
        : intFmt.format(value);
    default:
      return intFmt.format(value);
  }
}

export type DeltaTone = 'good' | 'bad' | 'neutral';

/**
 * Variação contra o período anterior. Sem base (anterior zero ou ausente) não inventa porcentagem:
 * diz "sem base de comparação" ou "novo".
 */
export function describeDelta(kpi: Pick<CentralKpi, 'value' | 'previous' | 'deltaPct' | 'higherIsBetter' | 'available'>): {
  text: string;
  tone: DeltaTone;
} {
  if (!kpi.available || kpi.value === null) return { text: '', tone: 'neutral' };
  if (kpi.deltaPct === null || kpi.deltaPct === undefined) {
    if ((kpi.previous ?? 0) === 0 && (kpi.value ?? 0) > 0) return { text: 'antes: 0', tone: 'neutral' };
    return { text: 'sem base de comparação', tone: 'neutral' };
  }
  if (kpi.deltaPct === 0) return { text: '0%', tone: 'neutral' };
  const up = kpi.deltaPct > 0;
  const good = up === kpi.higherIsBetter;
  return { text: `${up ? '+' : ''}${decFmt.format(kpi.deltaPct)}%`, tone: good ? 'good' : 'bad' };
}

/** "17/10 14:30" no fuso do navegador. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** "há 3 h", "há 2 dias", "em 4 dias". */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMin = Math.round((now.getTime() - d.getTime()) / 60000);
  const future = diffMin < 0;
  const abs = Math.abs(diffMin);
  let text: string;
  if (abs < 1) text = 'agora';
  else if (abs < 60) text = `${abs} min`;
  else if (abs < 60 * 24) text = `${Math.round(abs / 60)} h`;
  else text = `${Math.round(abs / (60 * 24))} dia${Math.round(abs / (60 * 24)) === 1 ? '' : 's'}`;
  if (text === 'agora') return text;
  return future ? `em ${text}` : `há ${text}`;
}

/** "17/10" a partir de "2026-10-17". */
export function formatDay(isoDate: string): string {
  const [, m, d] = isoDate.split('-');
  return d && m ? `${d}/${m}` : isoDate;
}
